import styled from 'styled-components';
import {
    _GetSelectedProduct,
    type IStockStatusViewModelMaybe,
    type StockStatusWithPreciseDeliveryAndModalViewModel
} from 'autogen/swagger/all';
import { Countdown } from 'components';
import { useProductPageExpectedDeliveryQuickView } from 'components/expecteddelivery/shared/hooks/useProductPageExpectedDeliveryQuickView';
import useNotificationFormQuickView from 'components/product/notification/shared/hooks/useNotificationFormQuickView';
import StatusItem from 'components/StatusItem';
import { FakeButton } from 'components/StockInfo/shared/styles';
import { useIsChanel } from 'hooks/user/useIsBrand';
import { formatReact } from 'modules/helpers/strings';
import { GA4Tracking } from 'modules/tracking/GA4';
import StoreStockInfo from '../StockInfoItem';

type ProductStockInfoRendererProps = {
    model: IStockStatusViewModelMaybe;
    productId: number;
};

const Info = styled.div`
    grid-area: info;
`;

const StyledCountdown = styled(Countdown)`
    width: auto;
`;

export default function ProductStockInfoRenderer(props: ProductStockInfoRendererProps) {
    const { model, productId } = props;
    const [, toggle] = useNotificationFormQuickView();
    const toggleExpextedDelivery = useProductPageExpectedDeliveryQuickView();
    const isChanel = useIsChanel();

    function hasExpectedDelivery(
        model: IStockStatusViewModelMaybe
    ): model is StockStatusWithPreciseDeliveryAndModalViewModel {
        return (model as StockStatusWithPreciseDeliveryAndModalViewModel).expectedDeliverySummary !== undefined;
    }

    const heading = hasExpectedDelivery(model) ? model.expectedDeliverySummary.heading : model.text;
    const expectedDeliverySummary = hasExpectedDelivery(model) && model.expectedDeliverySummary;
    const { mainText, buttonText, countdown } = expectedDeliverySummary || {};

    async function toggleNotificationForm() {
        await _GetSelectedProduct({ productId }).then((data) => {
            toggle({
                isOpen: true,
                notificationModel: { ...data, isPreorder: false }
            });
        });

        GA4Tracking({
            eventName: 'notification_request',
            category: 'ecommerce',
            context: 'product_page',
            eventParams: [{ param_name: 'notification_type', param_value: 'back_in_stock' }]
        });
    }

    async function toggleDeliveryInfo() {
        toggleExpextedDelivery(true);

        GA4Tracking({
            eventName: 'open_modal',
            category: 'ecommerce',
            context: 'product_page',
            eventParams: [{ param_name: 'modal_type', param_value: 'delivery_info' }]
        });
    }

    if (!heading) return;

    switch (model.state) {
        case 'Available':
            return (
                <StoreStockInfo
                    iconType="Delivery"
                    heading={heading}
                    onClick={toggleDeliveryInfo}
                    infoRenderer={() => (
                        <Info>
                            <StatusItem color={isChanel ? 'Black' : 'Green'} bulletSize={10}>
                                {!countdown &&
                                    (buttonText
                                        ? formatReact(mainText)(<FakeButton>{buttonText}</FakeButton>)
                                        : mainText)}
                                {countdown && (
                                    <div>
                                        {countdown.orderBeforeText}{' '}
                                        <StyledCountdown
                                            countdown={{
                                                finishDateTime: countdown.cutoff,
                                                sizeType: 'XS',
                                                showTimeUnits: true
                                            }}
                                        />
                                    </div>
                                )}
                            </StatusItem>
                        </Info>
                    )}
                />
            );
        case 'DropShip':
            return (
                <StoreStockInfo
                    iconType="Delivery"
                    heading={heading}
                    onClick={toggleDeliveryInfo}
                    infoRenderer={() => <Info>{mainText}</Info>}
                />
            );
        case 'Discontinued':
            return <StoreStockInfo iconType="Delivery" heading={heading} showArrow={false} />;
        case 'SoldOut':
            return (
                <StoreStockInfo
                    iconType="Bell"
                    heading={heading}
                    onClick={toggleNotificationForm}
                    {...(buttonText
                        ? {
                              infoRenderer: () => (
                                  <Info>{formatReact(mainText)(<FakeButton>{buttonText}</FakeButton>)}</Info>
                              )
                          }
                        : {})}
                />
            );
        default:
            return null;
    }
}
